import { useEffect, useCallback } from 'react';
import { useSocket } from '@/contexts/SocketContext';

interface SMSReceivedEvent {
  id: string;
  phoneNumber: string;
  message: string;
  receivedAt: string;
  contactName?: string;
  affaireId?: number;
}

interface SMSSentEvent {
  id: string;
  phoneNumber: string;
  message: string;
  sentAt: string;
  status: string;
}

interface SMSStatusUpdatedEvent {
  id: string;
  status: 'sent' | 'failed';
  updatedAt: string;
}

interface UseSMSLiveUpdatesOptions {
  onMessageReceived?: (event: SMSReceivedEvent) => void;
  onMessageSent?: (event: SMSSentEvent) => void;
  onStatusUpdated?: (event: SMSStatusUpdatedEvent) => void;
  selectedPhone?: string | null;
}

/**
 * Hook for live SMS updates in the SMS tab
 * Refreshes conversations when new messages arrive
 */
export const useSMSLiveUpdates = ({
  onMessageReceived,
  onMessageSent,
  onStatusUpdated,
  selectedPhone
}: UseSMSLiveUpdatesOptions = {}) => {
  const { socket, isConnected } = useSocket();

  // Handle received SMS
  const handleSMSReceived = useCallback((event: SMSReceivedEvent) => {
    console.log('[SMSLiveUpdates] SMS received:', event);
    if (onMessageReceived) {
      onMessageReceived(event);
    }
  }, [onMessageReceived]);

  // Handle sent SMS confirmation (when queued)
  const handleSMSSent = useCallback((event: SMSSentEvent) => {
    console.log('[SMSLiveUpdates] SMS sent:', event);
    if (onMessageSent) {
      onMessageSent(event);
    }
  }, [onMessageSent]);

  // Handle status update (when gateway confirms delivery)
  const handleStatusUpdated = useCallback((event: SMSStatusUpdatedEvent) => {
    console.log('[SMSLiveUpdates] SMS status updated:', event);
    if (onStatusUpdated) {
      onStatusUpdated(event);
    }
  }, [onStatusUpdated]);

  useEffect(() => {
    if (!socket || !isConnected) return;

    socket.on('sms:received', handleSMSReceived);
    socket.on('sms:sent', handleSMSSent);
    socket.on('sms:status-updated', handleStatusUpdated);

    return () => {
      socket.off('sms:received', handleSMSReceived);
      socket.off('sms:sent', handleSMSSent);
      socket.off('sms:status-updated', handleStatusUpdated);
    };
  }, [socket, isConnected, handleSMSReceived, handleSMSSent, handleStatusUpdated]);

  return {
    isConnected
  };
};

export default useSMSLiveUpdates;
